import React from 'react';
import { AlertTriangle, Bot, CircleUserRound, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OrderResponse } from '../../../services/OrderService';
import {
  ATTENTION_CHIP,
  ATTENTION_LABEL,
  STAGE_LABEL,
  STAGE_SEGMENT,
  STAGE_STYLE,
  STEPPER_SEGMENTS,
  formatDwell,
  hoursSince,
  isPastSla,
  ownerTypeOf,
  partsCounts,
  stageOf,
} from './orderLifecycle';

export { lifecycleRowClass } from './orderLifecycle';

/** Four-segment micro-stepper: Intake · Review · Cart · Ordered (spec §6). */
export const MicroStepper: React.FC<{ order: OrderResponse }> = ({ order }) => {
  const { t } = useTranslation();
  const stage = stageOf(order);
  const seg = STAGE_SEGMENT[stage];
  const flag = order.attention_flag;
  const cancelled = seg === 'cancelled';
  const done = stage === 'completed';
  const currentIdx = cancelled ? -1 : STEPPER_SEGMENTS.indexOf(seg as any);
  const fill = flag === 'system_error' ? '#EF4444' : flag ? '#F59E0B' : done ? '#0E6E58' : '#6866ff';
  return (
    <div
      className="flex items-center gap-0.5"
      role="img"
      aria-label={t('orders.stepper.aria', {
        defaultValue: 'Progress: {{segment}}',
        segment: cancelled ? t('orders.stage.cancelled', { defaultValue: 'Cancelled' }) : t(`orders.stepper.${seg}`, { defaultValue: seg }),
      })}
      title={STEPPER_SEGMENTS.map(s => t(`orders.stepper.${s}`, { defaultValue: s })).join(' · ')}
    >
      {STEPPER_SEGMENTS.map((s, i) => {
        const filled = !cancelled && (i < currentIdx || (i === currentIdx && (done || i < currentIdx + 1)));
        const active = i === currentIdx;
        return (
          <span
            key={s}
            className="h-1 w-3.5 rounded-sm"
            style={{
              background: cancelled ? '#E5E7EB' : filled ? fill : '#E5E7EB',
              opacity: active && !done ? 1 : filled ? 0.85 : 1,
            }}
          />
        );
      })}
    </div>
  );
};

const ownerMeta = (order: OrderResponse) => {
  const type = ownerTypeOf(order);
  if (type === 'unassigned') return { key: 'unassigned', fallback: 'Needs an owner', Icon: UserPlus, cls: 'text-[#8A5A00]' };
  if (type === 'person') return { key: 'person', fallback: order.owner_name || order.owner_uid || 'Assigned', Icon: CircleUserRound, cls: 'text-[#4B48C7]' };
  return { key: 'agent', fallback: 'Agent', Icon: Bot, cls: 'text-[#6B6862]' };
};

const OrderLifecycleCell: React.FC<{
  order: OrderResponse;
  compact?: boolean;
  onTake?: () => void;
  taking?: boolean;
  /** Hide the flag chip when the attention strip is rendered right under the row. */
  hideFlag?: boolean;
}> = ({ order, compact = false, onTake, taking = false, hideFlag = false }) => {
  const { t } = useTranslation();
  const stage = stageOf(order);
  const ownerType = ownerTypeOf(order);
  const owner = ownerMeta(order);
  const counts = partsCounts(order);
  const closed = stage === 'completed' || stage === 'cancelled';
  const dwell = closed ? '' : formatDwell(hoursSince(order.stage_changed_at || order.updated_at), t);
  const late = !!order.attention_flag && isPastSla(order);
  // The owner line only earns its place when it says something the Created By
  // column does not: a handover (needs an owner), the agent driving, or a
  // person other than the creator.
  const ownerName = order.owner_name || order.owner_uid || '';
  const showOwner = ownerType !== 'person' || (!!ownerName && ownerName !== (order.created_by_name || ''));

  return (
    <div className="flex flex-col items-start gap-0.5 min-w-0">
      {/* One line: chip · dwell (· flag). The owner only drops to a second line when it adds something. */}
      <div className="flex items-center gap-2 whitespace-nowrap">
        <span className={`inline-flex rounded-full border font-semibold whitespace-nowrap ${compact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-0.5 text-xs'} ${STAGE_STYLE[stage]}`}>
          {t(`orders.stage.${stage}`, { defaultValue: STAGE_LABEL[stage] })}
          {stage === 'in_cart' && counts && counts.inCart > 0 && (
            <span className="ml-1 opacity-80">· {counts.inCart}</span>
          )}
        </span>
        {dwell && (
          <span className={`text-[11px] ${late ? 'text-[#8A5A00] font-medium' : 'text-[#6B6862]'}`} title={t('orders.dwell.inStage', { defaultValue: 'Time in this stage' })}>
            {dwell}
          </span>
        )}
        {order.attention_flag && !hideFlag && (
          <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${ATTENTION_CHIP[order.attention_flag]}`}>
            <AlertTriangle className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
            {t(`orders.attention.${order.attention_flag}`, { defaultValue: ATTENTION_LABEL[order.attention_flag] })}
          </span>
        )}
      </div>
      {showOwner && (
        <span className={`inline-flex items-center gap-1 text-[11px] ${ownerType === 'unassigned' ? 'text-[#8A5A00] font-medium' : 'text-[#6B6862]'}`}>
          <owner.Icon className="h-3 w-3" aria-hidden="true" />
          <span className="truncate max-w-[10rem]">{owner.key === 'person' ? owner.fallback : t(`orders.owner.${owner.key}`, { defaultValue: owner.fallback })}</span>
        </span>
      )}
      {ownerType === 'unassigned' && onTake && (
        <button
          type="button"
          disabled={taking}
          onClick={(event) => { event.stopPropagation(); onTake(); }}
          className="inline-flex items-center rounded-md bg-[#8A5A00] px-2 py-1 text-[11px] font-semibold text-white hover:bg-[#704900] disabled:cursor-wait disabled:opacity-60"
        >
          {taking
            ? t('orders.owner.taking', { defaultValue: 'Taking…' })
            : t('orders.owner.takeIt', { defaultValue: 'Take it' })}
        </button>
      )}
    </div>
  );
};

export default OrderLifecycleCell;
